import { zodResolver } from "@hookform/resolvers/zod";
import { Edit01 } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { DialogTrigger, Heading } from "react-aria-components";
import { type SubmitHandler, useForm } from "react-hook-form";
import z from "zod/v3";
import { useCreateNavBadge, useUpdateNavBadge } from "@/api/admin/nav-badges";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Button } from "@/components/base/buttons/button";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { CloseButton } from "@/components/base/buttons/close-button";
import { Select } from "@/components/base/select/select";
import { Toggle } from "@/components/base/toggle/toggle";
import { NAV_ITEM_OPTIONS } from "@/components/sidebar/nav-item-options";
import { FormField, FormItem } from "@/components/ui/form";
import { error, success } from "@/providers/ToastProvider";

const schema = z.object({
  navItemId: z.string().min(1, "Pick the page this notification points at"),
  enabled: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

type NavBadgeData = {
  id: string;
  navItemId: string;
  enabled: boolean;
};

const selectItems = NAV_ITEM_OPTIONS.map((option) => ({
  id: option.id,
  label: `${option.group} › ${option.label}`,
}));

export const CreateNavBadgeModal = () => {
  const [open, setOpen] = useState(false);

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button>Add new Notification</Button>
      <NavBadgeModalContent mode="create" open={open} onClose={() => setOpen(false)} />
    </DialogTrigger>
  );
};

export const EditNavBadgeModal = ({ navBadge }: { navBadge: NavBadgeData }) => {
  const [open, setOpen] = useState(false);

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <ButtonUtility size="xs" color="tertiary" tooltip="Edit" icon={Edit01} onClick={() => setOpen(true)} />
      <NavBadgeModalContent mode="edit" open={open} onClose={() => setOpen(false)} navBadge={navBadge} />
    </DialogTrigger>
  );
};

type ModalContentProps = {
  mode: "create" | "edit";
  open: boolean;
  onClose: () => void;
  navBadge?: NavBadgeData;
};

const NavBadgeModalContent = ({ mode, open, onClose, navBadge }: ModalContentProps) => {
  const isEdit = mode === "edit";
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      navItemId: navBadge?.navItemId ?? "",
      enabled: navBadge?.enabled ?? true,
    },
  });

  const { mutateAsync: create } = useCreateNavBadge();
  const { mutateAsync: update } = useUpdateNavBadge();

  useEffect(() => {
    form.reset({
      navItemId: navBadge?.navItemId ?? "",
      enabled: navBadge?.enabled ?? true,
    });
  }, [open]);

  const onSubmit: SubmitHandler<FormValues> = async (values) => {
    try {
      if (isEdit && navBadge) {
        await update({ id: navBadge.id, navItemId: values.navItemId, enabled: values.enabled });
        success("Notification updated");
      } else {
        await create({ navItemId: values.navItemId, enabled: values.enabled });
        success("Notification created");
      }
      onClose();
    } catch (err) {
      error(err instanceof Error ? err.message : "Could not save the notification");
    }
  };

  return (
    <ModalOverlay isDismissable>
      <Modal className="dark-mode max-w-lg">
        <Dialog>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="relative w-full overflow-hidden rounded-2xl bg-primary shadow-none transition-all sm:w-full">
              <CloseButton onClick={onClose} theme="light" size="lg" className="absolute top-3 right-3" />
              <div className="flex flex-col gap-4 px-4 pt-5 mr-4 sm:px-6 sm:pt-6">
                <div className="z-10 flex flex-col gap-0.5">
                  <Heading slot="title" className="text-md font-semibold text-primary">
                    {isEdit ? "Edit New Page Notification" : "Add new Notification"}
                  </Heading>
                  <p className="text-sm text-tertiary">
                    The dot shows on this page in every user&rsquo;s nav until they visit it, or for 14 days from now,
                    whichever comes first. Three at most are ever shown at once.
                  </p>
                </div>
              </div>
              <div className="h-5 w-full" />
              <div className="relative flex flex-col gap-4 px-4 sm:gap-5 sm:px-6">
                <FormField
                  control={form.control}
                  name="navItemId"
                  render={({ field, fieldState }) => (
                    <FormItem className="flex flex-col">
                      <Select
                        label="Page"
                        placeholder="Select a page"
                        isRequired
                        size="md"
                        items={selectItems}
                        selectedKey={field.value || null}
                        onSelectionChange={(value) => field.onChange(value)}
                        isInvalid={!!fieldState.error}
                        hint={fieldState.error?.message}
                        popoverClassName="dark-mode"
                      >
                        {(item) => (
                          <Select.Item key={item.id} id={item.id} className="dark-mode">
                            {item.label}
                          </Select.Item>
                        )}
                      </Select>
                    </FormItem>
                  )}
                />

                <div className="flex flex-col gap-3 rounded-lg bg-secondary p-4">
                  <Toggle
                    size="md"
                    isSelected={form.watch("enabled")}
                    onChange={(checked) => form.setValue("enabled", checked)}
                    label="Show the notification"
                    hint="Turn this off to pull the dot without deleting the record."
                    className="[&>div>p]:text-sm! [&>div>span]:text-sm! [&>div>span]:text-wrap [&>div>span]:max-w-[400px]"
                  />
                </div>
              </div>
              <div className="z-10 flex flex-1 flex-col-reverse gap-3 p-4 pt-6 *:grow sm:grid sm:grid-cols-2 sm:px-6 sm:pt-8 sm:pb-6">
                <Button color="secondary" size="lg" onClick={onClose}>
                  Cancel
                </Button>
                <Button color="primary" size="lg" type="submit">
                  Save Notification
                </Button>
              </div>
            </div>
          </form>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
